import { memo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { useLoadCargo, useLoadWagonType, useLoadWagonModel } from "@/common/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  Textarea,
  SearchableCombobox,
} from "@/common/components";

const LIMIT = 100;
const PAGE = 1;

export const CargoParamsCard = memo(() => {
  const [cargoSearch, setCargoSearch] = useState("");
  const [cargoOptions, setCargoOptions] = useState<Record<"value" | "label", string>[]>([]);

  const [wagonTypeOptions, setWagonTypeOptions] = useState<Record<"value" | "label", string>[]>([]);
  const [wagonSearch, setWagonSearch] = useState("");
  const [wagonModelOptions, setWagonModelOptions] = useState<Record<"value" | "label", string>[]>(
    [],
  );

  const { control, setValue, watch, trigger } = useFormContext();

  const watchCargoId = watch("cargoId");
  const watchWagonType = watch("wagonType");
  const watchWagonCode = watch("wagonCode");

  const cargoQuery = useLoadCargo({
    name: cargoSearch,
    code: watchCargoId,
    page: PAGE,
    limit: LIMIT,
  });

  const wagonTypeQuery = useLoadWagonType();

  const wagonModelQuery = useLoadWagonModel(
    {
      type: watchWagonType,
      model: wagonSearch,
      page: PAGE,
      limit: LIMIT,
    },
    {
      query: {
        enabled: Boolean(watchWagonType),
      },
    },
  );

  useEffect(() => {
    const cargoItems = cargoQuery.data?.data.payload?.items;

    if (cargoItems) {
      setCargoOptions(
        cargoItems.map((item) => ({
          value: item.cargoCode ?? "",
          label: item.cargoName ?? "",
        })),
      );
    }
  }, [cargoQuery.data]);

  useEffect(() => {
    const wagonModels = wagonModelQuery.data?.data.payload?.items;

    if (wagonModels) {
      setWagonModelOptions(
        wagonModels.map((item) => ({
          value: item.code ?? "",
          label: item.model ?? "",
        })),
      );
    }
  }, [wagonModelQuery.data]);

  useEffect(() => {
    const wagonTypeItems = wagonTypeQuery.data?.data.payload;

    if (wagonTypeItems) {
      setWagonTypeOptions(
        wagonTypeItems.map((item) => ({
          value: item ?? "",
          label: item ?? "",
        })),
      );
    }
  }, [wagonTypeQuery.data]);

  useEffect(() => {
    const wagonModel = wagonModelQuery.data?.data.payload?.items?.find(
      (item) => item.code === watchWagonCode,
    );

    if (wagonModel && watchWagonCode) {
      setValue("wagonCarrying", wagonModel.liftingCapacity ?? "");
      setValue("wagonVolume", wagonModel.bodySize ?? "");
      trigger(["wagonCarrying", "wagonVolume"]);
    } else if (!watchWagonCode) {
      setValue("wagonCarrying", 0);
      setValue("wagonVolume", 0);
      trigger(["wagonCarrying", "wagonVolume"]);
    }
  }, [watchWagonCode, wagonModelQuery.data, setValue, trigger]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Параметры груза</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 md:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 ">
          <div className="md:col-span-2">
            <FormField
              control={control}
              name="cargoId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Наименование груза *</FormLabel>
                  <FormControl>
                    <SearchableCombobox
                      options={cargoOptions}
                      value={watchCargoId}
                      onValueChange={(value) => {
                        field.onChange(value);

                        setCargoSearch("");

                        setValue("cargoId", value);
                        setValue("cargoCode", value);
                      }}
                      onSearchChange={(value) => {
                        setCargoSearch(value);

                        setValue("cargoId", "");
                        setValue("cargoCode", "");
                      }}
                      placeholder="Выберите груз"
                      searchPlaceholder="Поиск груза..."
                      emptyText="Груз не найден"
                      className="text-sm"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:col-span-1">
            <FormField
              control={control}
              name="cargoCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Код ЕТСНГ *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Код" disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:col-span-1">
            <FormField
              control={control}
              name="cargoWeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Вес (т) *</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="Вес" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="md:col-span-1">
            <FormField
              control={control}
              name="wagonType"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Тип вагона *</FormLabel>
                    <Select
                      key={watchWagonType}
                      defaultValue={watchWagonType}
                      onValueChange={(value) => {
                        field.onChange(value);

                        setWagonSearch("");

                        setValue("wagonType", value);
                        setValue("wagonCode", "");
                      }}
                      placeholder={
                        wagonTypeQuery.isLoading ? "Идет загрузка справочника..." : "Выберите тип"
                      }
                    >
                      <SelectContent>
                        <SelectItem value="">Выберите тип</SelectItem>
                        {wagonTypeOptions.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <div className="md:col-span-1">
            <FormField
              control={control}
              name="wagonCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Модель вагона</FormLabel>
                  <FormControl>
                    <SearchableCombobox
                      options={wagonModelOptions}
                      value={watchWagonCode}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setValue("wagonCode", value);
                      }}
                      onSearchChange={(value: string) => {
                        setWagonSearch(value);
                        setValue("wagonCode", "");
                      }}
                      placeholder="Выберите модель вагона"
                      searchPlaceholder="Поиск модели вагона..."
                      emptyText="Модель вагона не найдена"
                      className="text-sm"
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={control}
            name="modelId"
            render={({ field }) => <input {...field} className="hidden" disabled />}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="md:col-span-1">
            <FormField
              control={control}
              name="wagonCarrying"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Грузоподъемность (т)</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="Введите вес" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:col-span-1">
            <FormField
              control={control}
              name="wagonVolume"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Объем (м3)</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="Введите объем" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormField
          control={control}
          name="cargoComments"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Дополнительные требования по вагонам</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Например: высота борта, люка и пр"
                  {...field}
                  value={field.value ?? ""}
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
});
